// packages/shared/src/schemas/competitive.ts

import { z } from 'zod';

export const CreateMatchResultSchema = z.object({
  venueId: z.string().uuid(),
  gameType: z.enum(['SNOOKER', 'GAMING', 'KIDS_PLAYAREA']),
  player1Id: z.string().uuid(),
  player2Id: z.string().uuid(),
  outcome: z.enum(['WIN', 'LOSS', 'DRAW']), // from player1's perspective
  bookingId: z.string().uuid().optional(),
  player1Score: z.number().int().min(0).optional(),
  player2Score: z.number().int().min(0).optional(),
  stats: z.record(z.string(), z.number()).optional(), // e.g. { BEST_BREAK: 87, CENTURY_COUNT: 0 }
  notes: z.string().max(500).optional(),
});

export type CreateMatchResultDto = z.infer<typeof CreateMatchResultSchema>;

export const UpdateMatchResultSchema = z.object({
  outcome: z.enum(['WIN', 'LOSS', 'DRAW']).optional(),
  player1Score: z.number().int().min(0).optional(),
  player2Score: z.number().int().min(0).optional(),
  stats: z.record(z.string(), z.number()).optional(),
  notes: z.string().max(500).optional(),
  isVerified: z.boolean().optional(),
});

export type UpdateMatchResultDto = z.infer<typeof UpdateMatchResultSchema>;

export const ConfirmMatchResultSchema = z.object({
  confirmed: z.boolean(),
  disputeReason: z.string().max(500).optional(),
});

export type ConfirmMatchResultDto = z.infer<typeof ConfirmMatchResultSchema>;

export const MatchResultResponseSchema = z.object({
  id: z.string().uuid(),
  venueId: z.string().uuid(),
  gameType: z.enum(['SNOOKER', 'GAMING', 'KIDS_PLAYAREA']),
  player1Id: z.string().uuid(),
  player2Id: z.string().uuid(),
  winnerId: z.string().uuid().nullable(),
  outcome: z.enum(['WIN', 'LOSS', 'DRAW']),
  player1Score: z.number().nullable(),
  player2Score: z.number().nullable(),
  entrySource: z.enum(['STAFF', 'PLAYER', 'SYSTEM']),
  isVerified: z.boolean(),
  isDeleted: z.boolean(),
  bookingId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  disputedAt: z.string().nullable(),
});

export type MatchResultResponseDto = z.infer<typeof MatchResultResponseSchema>;

export const CompetitiveProfileResponseSchema = z.object({
  userId: z.string().uuid(),
  venueId: z.string().uuid(),
  gameType: z.enum(['SNOOKER', 'GAMING', 'KIDS_PLAYAREA']),
  wins: z.number().int(),
  losses: z.number().int(),
  draws: z.number().int(),
  winRate: z.number(),
  currentStreak: z.number().int(),
  bestStreak: z.number().int(),
  records: z.array(z.object({
    recordType: z.string(),
    value: z.number(),
    achievedAt: z.string(),
  })),
  h2hRivals: z.array(z.object({
    opponentId: z.string().uuid(),
    opponentName: z.string(),
    wins: z.number().int(),
    losses: z.number().int(),
    draws: z.number().int(),
    lastMatchAt: z.string().nullable(),
  })),
  recentMatches: z.array(z.object({
    id: z.string().uuid(),
    opponentId: z.string().uuid(),
    opponentName: z.string(),
    outcome: z.enum(['WIN', 'LOSS', 'DRAW']),
    gameType: z.enum(['SNOOKER', 'GAMING', 'KIDS_PLAYAREA']),
    playedAt: z.string(),
    isVerified: z.boolean(),
  })),
});

export type CompetitiveProfileResponseDto = z.infer<typeof CompetitiveProfileResponseSchema>;

export const VenueRecordResponseSchema = z.object({
  venueId: z.string().uuid(),
  gameType: z.enum(['SNOOKER', 'GAMING', 'KIDS_PLAYAREA']),
  recordType: z.string(),
  value: z.number(),
  holderId: z.string().uuid(),
  holderName: z.string(),
  achievedAt: z.string(),
});

export type VenueRecordResponseDto = z.infer<typeof VenueRecordResponseSchema>;
